/**
 * Generic Integration Item Picker
 * Reusable picker for Notion pages, Fathom/Gong/Fireflies/Avoma meetings/calls
 * Same visual style as TldvMeetingPicker
 */

import { useState, useEffect, useRef } from 'react';
import {
  Check,
  X,
  Loader2,
  Search,
  FileText,
} from 'lucide-react';
import type { PickerItem } from '../lib/api';

interface IntegrationItemPickerProps {
  title: string;              // "Select Notion Pages"
  itemLabel: string;          // "page" / "meeting" / "call"
  sourceType: string;         // "notion" / "fathom" etc.
  onClose: () => void;
  onSave: (items: PickerItem[]) => void;
  initialSelected?: PickerItem[];
  fetchItems: (params: { page: number; limit: number; query?: string }) => Promise<{
    items: PickerItem[];
    total: number;
    pages: number;
  }>;
}

export function IntegrationItemPicker({
  title,
  itemLabel,
  onClose,
  onSave,
  initialSelected = [],
  fetchItems,
}: IntegrationItemPickerProps) {
  const [items, setItems] = useState<PickerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [selectedById, setSelectedById] = useState<Record<string, PickerItem>>(() => {
    const initial: Record<string, PickerItem> = {};
    for (const item of initialSelected) {
      initial[item.id] = item;
    }
    return initial;
  });

  useEffect(() => {
    loadItems();
  }, [page]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      loadItems();
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchItems({
        page,
        limit: 20,
        query: searchQuery || undefined,
      });
      setItems(result.items);
      setTotalPages(result.pages);
    } catch {
      setError('Failed to load items');
    }
    setLoading(false);
  };

  const toggleItem = (item: PickerItem) => {
    setSelectedById(prev => {
      if (item.id in prev) {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      } else {
        return { ...prev, [item.id]: item };
      }
    });
  };

  const toggleAll = () => {
    const allCurrentSelected = items.every(item => item.id in selectedById);
    if (allCurrentSelected) {
      // Deselect all items on current page
      setSelectedById(prev => {
        const next = { ...prev };
        for (const item of items) {
          delete next[item.id];
        }
        return next;
      });
    } else {
      // Select all items on current page
      setSelectedById(prev => {
        const next = { ...prev };
        for (const item of items) {
          next[item.id] = item;
        }
        return next;
      });
    }
  };

  const handleSave = () => {
    onSave(Object.values(selectedById));
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const selectedCount = Object.keys(selectedById).length;
  const allCurrentSelected = items.length > 0 && items.every(item => item.id in selectedById);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${itemLabel}s...`}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-mazo-500 focus:border-mazo-500"
            />
          </div>
        </div>

        {/* Select all */}
        {!loading && items.length > 0 && (
          <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
            <button
              onClick={toggleAll}
              className="text-xs text-mazo-600 hover:text-mazo-800 font-medium"
            >
              {allCurrentSelected ? 'Deselect all' : 'Select all on this page'}
            </button>
            {selectedCount > 0 && (
              <span className="text-xs text-gray-500 bg-mazo-100 text-mazo-700 px-2 py-0.5 rounded-full font-medium">
                {selectedCount} selected
              </span>
            )}
          </div>
        )}

        {/* Item List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-mazo-600" />
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600">{error}</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No {itemLabel}s found</p>
            </div>
          ) : (
            <div className="divide-y">
              {items.map((item) => {
                const isSelected = item.id in selectedById;
                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => toggleItem(item)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-mazo-600 border-mazo-600 text-white'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        {item.date && <span>{formatDate(item.date)}</span>}
                        {item.date && item.subtitle && <span className="text-gray-300">|</span>}
                        {item.subtitle && <span>{item.subtitle}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-2 border-t bg-gray-50 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedCount} {itemLabel}{selectedCount !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={selectedCount === 0}
              className="px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
